// /app/frontend/src/pages/admin/AdminLoveCoinsPage.jsx
/**
 * Admin LoveCoins Management Page
 * 
 * - Search users by email/ID
 * - View balance and monthly cap status
 * - Credit users with audit trail
 * - View ledger entries
 * - Gift message templates
 */

import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/client";
import { AuthContext } from "../../App";
import { 
  ArrowLeft, 
  Search, 
  Coins, 
  Gift,
  Clock,
  AlertTriangle,
  CheckCircle,
  User,
  Send,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { createInternalAxiosConfig } from "../../utils/internalApi";
import AdminHelp from "../../components/admin/AdminHelp";


export default function AdminLoveCoinsPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [config, setConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Credit form
  const [creditAmount, setCreditAmount] = useState("");
  const [creditReason, setCreditReason] = useState("");
  const [creditSource, setCreditSource] = useState("support");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [showTemplatePreview, setShowTemplatePreview] = useState(false);

  // Load config and templates on mount
  useEffect(() => {
    async function loadInitial() {
      try {
        const axiosConfig = createInternalAxiosConfig(token, 'admin');
        const [configRes, templatesRes] = await Promise.all([
          apiClient.get(`/admin/lovecoins/config`, axiosConfig),
          apiClient.get(`/admin/lovecoins/templates`, axiosConfig),
        ]);
        setConfig(configRes.data);
        setTemplates(templatesRes.data.templates || []);
      } catch (err) {
        console.error("[AdminLoveCoins] Failed to load config:", err);
      }
    }
    loadInitial();
  }, [token]);

  // Search users
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const axiosConfig = createInternalAxiosConfig(token, 'admin');
      const res = await apiClient.get(
        `/admin/lovecoins/search?q=${encodeURIComponent(searchQuery)}`,
        axiosConfig
      );
      setSearchResults(res.data.users || []);
      setSelectedUser(null);
      setUserDetails(null);
    } catch (err) {
      setError(err.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  // Select user and load details
  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setLoading(true);
    try {
      const axiosConfig = createInternalAxiosConfig(token, 'admin');
      const res = await apiClient.get(
        `/admin/lovecoins/user/${user.id}`,
        axiosConfig
      );
      setUserDetails(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  // Credit user
  const handleCredit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !creditAmount || !creditReason) return;
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const axiosConfig = createInternalAxiosConfig(token, 'admin');
      const res = await apiClient.post(`/admin/lovecoins/credit`, {
        target_user_id: selectedUser.id,
        amount: parseInt(creditAmount),
        reason: creditReason,
        source: creditSource,
        template_key: selectedTemplate || undefined,
        admin_note: adminNote || undefined,
      }, axiosConfig);
      
      setSuccess(`Credited ${creditAmount} coins! New balance: ${res.data.new_balance}`);
      setCreditAmount("");
      setCreditReason("");
      setSelectedTemplate("");
      setAdminNote("");
      
      // Refresh user details
      handleSelectUser(selectedUser);
    } catch (err) {
      setError(err.response?.data?.detail || "Credit failed");
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplateData = templates.find(t => t.key === selectedTemplate);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <header className="border-b border-slate-800 pb-4">
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center space-x-2 text-sm text-slate-400 hover:text-slate-200 transition mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Admin</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                  <Coins className="w-6 h-6 text-amber-400" />
                  LoveCoins Admin
                </h1>
                <AdminHelp helpKey="lovecoins" />
              </div>
              <p className="text-sm text-slate-400 mt-1">
                Credit, audit, and manage LoveCoins balances
              </p>
            </div>
            {config?.production_guard_active && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-3 py-1.5 text-sm text-red-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Admin credits disabled in production
              </div>
            )}
          </div>
        </header>

        {/* Config Info */}
        {config && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4 text-sm">
            <div className="flex gap-6">
              <div>
                <span className="text-slate-400">Monthly Cap:</span>{" "}
                <span className="text-amber-400 font-medium">{config.max_monthly_promo_credit} coins</span>
              </div>
              <div>
                <span className="text-slate-400">Per TX Limit:</span>{" "}
                <span className="text-amber-400 font-medium">{config.max_credit_per_tx} coins</span>
              </div>
              <div>
                <span className="text-slate-400">Environment:</span>{" "}
                <span className={config.environment === "production" ? "text-red-400" : "text-green-400"}>
                  {config.environment}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 text-red-300">
            {typeof error === 'object' ? JSON.stringify(error) : error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-4 py-2 text-green-300 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            {success}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Search + Results */}
          <div className="space-y-4">
            {/* Search */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                Search User
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Email or User ID"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-medium transition disabled:opacity-50"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Results ({searchResults.length})</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleSelectUser(user)}
                      className={`w-full text-left px-3 py-2 rounded-lg transition ${
                        selectedUser?.id === user.id
                          ? "bg-purple-600/30 border border-purple-500"
                          : "bg-slate-800/50 hover:bg-slate-800"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium">{user.email}</p>
                          <p className="text-xs text-slate-500">{user.id.slice(0, 8)}...</p>
                        </div>
                        <div className="text-right">
                          <p className="text-amber-400 font-medium">{user.lovecoins_balance || 0}</p>
                          <p className="text-xs text-slate-500">coins</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* User Details */}
            {userDetails && (
              <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  User Details
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Balance</span>
                    <span className="text-amber-400 font-bold text-lg">{userDetails.current_balance}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Credited This Month</span>
                    <span>{userDetails.cap_status?.credited_this_month || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Remaining Allowance</span>
                    <span className="text-green-400">{userDetails.cap_status?.remaining_allowance || 0}</span>
                  </div>
                </div>

                {/* Recent Ledger */}
                {userDetails.recent_ledger?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      Recent Ledger
                    </h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {userDetails.recent_ledger.map((entry, i) => (
                        <div key={i} className="flex justify-between text-xs bg-slate-800/50 rounded px-2 py-1">
                          <span className="text-slate-400">{entry.event_type}</span>
                          <span className={entry.delta > 0 ? "text-green-400" : "text-red-400"}>
                            {entry.delta > 0 ? "+" : ""}{entry.delta}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right: Credit Form */}
          <div className="space-y-4">
            <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Gift className="w-4 h-4 text-pink-400" />
                Credit LoveCoins
              </h3>
              
              {!selectedUser ? (
                <p className="text-slate-500 text-sm">Select a user to credit coins</p>
              ) : (
                <form onSubmit={handleCredit} className="space-y-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Target User</label>
                    <div className="bg-slate-800/50 rounded px-3 py-2 text-sm">
                      {selectedUser.email}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Amount *</label>
                    <input
                      type="number"
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(e.target.value)}
                      min="1"
                      max={config?.max_credit_per_tx || 1000}
                      required
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder={`Max ${config?.max_credit_per_tx || 1000}`}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Source *</label>
                    <select
                      value={creditSource}
                      onChange={(e) => setCreditSource(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="support">Support</option>
                      <option value="promo">Promo</option>
                      <option value="testing">Testing</option>
                      <option value="refund_adjustment">Refund Adjustment</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Reason *</label>
                    <input
                      type="text"
                      value={creditReason}
                      onChange={(e) => setCreditReason(e.target.value)}
                      required
                      minLength={5}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Why are you crediting this user?"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Gift Message Template (optional)</label>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => setSelectedTemplate(e.target.value)}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">No message</option>
                      {templates.filter(t => t.is_active).map((t) => (
                        <option key={t.key} value={t.key}>{t.title}</option>
                      ))}
                    </select>
                  </div>

                  {selectedTemplate && (
                    <div>
                      <button
                        type="button"
                        onClick={() => setShowTemplatePreview(!showTemplatePreview)}
                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                      >
                        {showTemplatePreview ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        Preview template
                      </button>
                      {showTemplatePreview && selectedTemplateData && (
                        <div className="mt-2 bg-slate-800/50 rounded-lg p-3 text-xs">
                          <p className="font-medium text-purple-300">{selectedTemplateData.title}</p>
                          <p className="mt-1 text-slate-400">{selectedTemplateData.body}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTemplate && (
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Admin Note (optional, 280 chars)</label>
                      <textarea
                        value={adminNote}
                        onChange={(e) => setAdminNote(e.target.value.slice(0, 280))}
                        maxLength={280}
                        rows={2}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Add a personal note (appended to template)"
                      />
                      <p className="text-xs text-slate-500 mt-1">{adminNote.length}/280</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || config?.production_guard_active}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                    Credit {creditAmount || "0"} Coins
                  </button>
                </form>
              )}
            </div>

            {/* Templates List */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-lg p-4">
              <h3 className="font-semibold mb-3 text-sm">Available Templates</h3>
              <div className="space-y-2 text-xs">
                {templates.map((t) => (
                  <div key={t.key} className={`px-2 py-1.5 rounded ${t.is_active ? 'bg-slate-800/50' : 'bg-slate-800/20 opacity-50'}`}>
                    <span className="font-medium">{t.key}</span>
                    <span className="text-slate-500 ml-2">— {t.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
